import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useVisitorContext } from "@/contexts/VisitorTrackingContext";
import { decidePrompt } from "@/lib/consentDecision";
import { ConsentNotice } from "@/components/consent/ConsentNotice";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Cookie, Shield, BarChart3, Bot } from "lucide-react";

/** First-party functional cookies removed when a US visitor opts out. */
const FUNCTIONAL_COOKIES = ["tango_visitor_id", "tango_persona"];
function removeFunctionalCookies() {
  for (const name of FUNCTIONAL_COOKIES) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
  }
}

export function CookieConsent() {
  const consent = useCookieConsent();
  const { hasConsented, functional, analytics, acceptAll, acceptEssentialOnly, updatePreferences, consentLoaded, geo, gpc } = consent;
  const visitor = useVisitorContext();
  const location = useLocation();
  const [showPrefs, setShowPrefs] = useState(false);
  const [localFunctional, setLocalFunctional] = useState(true); // pre-toggled ON
  const [localAnalytics, setLocalAnalytics] = useState(false);
  // Esc / × hides the email prompt or US notice for this page view only.
  const [dismissedPath, setDismissedPath] = useState<string | null>(null);
  useEffect(() => { setDismissedPath(null); }, [location.pathname]);

  const prompt = consentLoaded
    ? decidePrompt({ stored: hasConsented ? { functional, analytics, source: "user" } : null, geo, gpc, hasPendingToken: visitor.hasPendingTc })
    : "wait";

  if ((prompt === "none" || prompt === "wait") && !showPrefs) return null;

  const handleSavePrefs = () => {
    updatePreferences({ functional: localFunctional, analytics: localAnalytics });
    if (!localFunctional) { visitor.discardPendingTc(); if (consent.usDefaultActive) removeFunctionalCookies(); }
    setShowPrefs(false);
  };

  const essentialOnly = () => {
    acceptEssentialOnly();
    visitor.discardPendingTc();
  };

  const openPrefs = () => {
    // Sync local state with current preferences (or defaults for first visit)
    setLocalFunctional(hasConsented || consent.usDefaultActive ? functional : true);
    setLocalAnalytics(hasConsented ? analytics : false);
    setShowPrefs(true);
  };

  const dismissed = dismissedPath === location.pathname;
  const dismiss = () => setDismissedPath(location.pathname);

  const equalBtn = "flex-1 sm:flex-initial min-w-[8rem]";
  const linkCls = "text-accent hover:underline font-medium";

  return (
    <>
      {/* Item 100 — US notice (functional on by default, analytics off) */}
      {prompt === "us_notice" && !dismissed && !showPrefs && (
        <ConsentNotice
          title="How we use cookies on this site"
          onDismiss={dismiss}
          actions={
            <>
              <Button variant="outline" size="sm" className={equalBtn}
                onClick={() => updatePreferences({ functional: true, analytics: false })}>
                OK
              </Button>
              <Button variant="outline" size="sm" className={equalBtn}
                onClick={() => { essentialOnly(); removeFunctionalCookies(); }}>
                Opt out
              </Button>
            </>
          }
          footer={<button type="button" onClick={openPrefs} className={linkCls}>Manage</button>}
        >
          We remember your visits to this site so we can understand what interests you. No advertising or analytics cookies unless you choose them.{" "}
          <Link to="/cookies" className={linkCls}>Cookie Policy</Link>
        </ConsentNotice>
      )}

      {/* Item 99 — visitor arrived from one of our emails */}
      {prompt === "email_prompt" && !dismissed && !showPrefs && (
        <ConsentNotice
          title="Linking your visit from our email"
          onDismiss={dismiss}
          actions={
            <>
              <Button variant="outline" size="sm" className={equalBtn}
                onClick={() => updatePreferences({ functional: true, analytics: false })}>
                Yes, that's fine
              </Button>
              <Button variant="outline" size="sm" className={equalBtn} onClick={essentialOnly}>
                No thanks
              </Button>
            </>
          }
          footer={<button type="button" onClick={openPrefs} className={linkCls}>Cookie settings</button>}
        >
          Thanks for clicking through from our email. May we remember this visit so we can follow up on what interests you?{" "}
          <Link to="/privacy#website-visits-and-marketing-emails" className={linkCls}>Privacy Policy</Link>
        </ConsentNotice>
      )}

      {/* General banner — unchanged */}
      {prompt === "banner" && (
        <div className="fixed bottom-0 inset-x-0 z-[60] bg-background/95 backdrop-blur-sm border-t border-border shadow-lg animate-slide-up">
          <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Cookie className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground leading-relaxed">
                We use cookies to personalise your experience with our AI assistant Tanya and to understand how visitors use our site.{" "}
                <button onClick={openPrefs} className="text-accent hover:underline font-medium">
                  Learn more
                </button>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={openPrefs} className="flex-1 sm:flex-initial">
                Manage
              </Button>
              <Button variant="accent" size="sm" onClick={acceptAll} className="flex-1 sm:flex-initial">
                Accept All
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences dialog */}
      <Dialog open={showPrefs} onOpenChange={setShowPrefs}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-accent" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Choose which cookies you'd like to allow. Your preferences are saved locally on your device.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Essential — always on */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50">
              <div className="flex items-start gap-3">
                <Shield className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Essential</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Required for the website to function. Includes layout preferences and authentication.
                  </p>
                </div>
              </div>
              <Switch checked disabled className="opacity-60" />
            </div>

            {/* Functional */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-accent/20 bg-accent/5">
              <div className="flex items-start gap-3">
                <Bot className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Functional / Personalisation</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Enables our AI assistant Tanya to remember your name and personalise your experience across visits.
                  </p>
                </div>
              </div>
              <Switch checked={localFunctional} onCheckedChange={setLocalFunctional} />
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50">
              <div className="flex items-start gap-3">
                <BarChart3 className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Analytics</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Helps us understand how visitors use our site so we can improve it. Currently not active.
                  </p>
                </div>
              </div>
              <Switch checked={localAnalytics} onCheckedChange={setLocalAnalytics} />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" size="sm" onClick={() => { const wasUsDefault = consent.usDefaultActive; setShowPrefs(false); essentialOnly(); if (wasUsDefault) removeFunctionalCookies(); }}>
              Essential Only
            </Button>
            <Button variant="accent" size="sm" onClick={handleSavePrefs}>
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
